import React, { useState, useEffect } from "react";
import ConflictActionButton from "./ConflictActionButton";

interface ConflictModalProps {
  conflictedFiles: File[];
  conflictActions: Record<string, "overwrite" | "copy" | "cancel">;
  onActionChange: (
    fileName: string,
    action: "overwrite" | "copy" | "cancel"
  ) => void;
  onSingleFileProcess: (
    file: File,
    action: "overwrite" | "copy" | "cancel"
  ) => Promise<void>;
  onComplete: () => void;
  dismissConflictModal: () => void;
  fetchFiles: () => void;
  clearConflictActions: () => void;
}

const ConflictModal: React.FC<ConflictModalProps> = ({
  conflictedFiles,
  conflictActions,
  onActionChange,
  onSingleFileProcess,
  onComplete,
  dismissConflictModal,
  clearConflictActions,
}) => {
  const isSingleConflict = conflictedFiles.length === 1;
  const singleFile = conflictedFiles[0];

  const [processingIndex, setProcessingIndex] = useState<number | null>(null);
  const [doneFiles, setDoneFiles] = useState<string[]>([]);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);

  const handleSingleAction = async (
    action: "overwrite" | "copy" | "cancel"
  ) => {
    setTimeout(() => {
      dismissConflictModal();
    }, 250);
    await onSingleFileProcess(singleFile, action);
  };

  const [isProcessing, setIsProcessing] = useState(false);

  const handleMultiFileProcess = async () => {
    setIsProcessing(true);

    for (let i = 0; i < conflictedFiles.length; i++) {
      const file = conflictedFiles[i];
      const action = conflictActions[file.name];

      if (!action || action === "cancel") continue;
      setProcessingIndex(i);
      try {
        await onSingleFileProcess(file, action);
        setDoneFiles((prev) => [...prev, file.name]);
      } catch (error) {
        console.error(`Error processing file ${file.name}:`, error);
        setFailedFiles((prev) => [...prev, file.name]);
      }
    }

    setProcessingIndex(null);

    const actionableFiles = conflictedFiles.filter((file) => {
      const action = conflictActions[file.name];
      return action && action !== "cancel";
    });

    if (actionableFiles.length === 0) dismissConflictModal();
    else {
      setTimeout(() => {
        onComplete();
      }, 1500);
    }
  };

  const allActionsSelected = conflictedFiles.every(
    (file) => conflictActions[file.name] !== undefined
  );

  useEffect(() => clearConflictActions(), []);

  const disabled = processingIndex !== null || !allActionsSelected;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="bg-white rounded-lg py-4 w-full max-w-lg flex flex-col">
        <h2 className="text-lg font-semibold border-b border-gray-300 px-4 pb-4">
          {isSingleConflict ? "File Conflict" : "File Conflicts"}
        </h2>

        {isSingleConflict ? (
          <>
            <p className="px-4 mt-4">
              The file <strong>{singleFile.name}</strong> already exists. What
              would you like to do?
            </p>
            <div className="flex justify-between space-x-3 border-t border-gray-300 pt-4 mt-4 px-4">
              <button
                type="button"
                className="text-white bg-red-600 rounded px-4 py-2"
                onClick={dismissConflictModal}
              >
                Cancel
              </button>
              <div className="flex space-x-3">
                <button
                  type="button"
                  className="text-white bg-blue-600 rounded px-4 py-2"
                  onClick={() => handleSingleAction("copy")}
                >
                  Copy
                </button>
                <button
                  type="button"
                  className="text-white bg-green-600 rounded px-4 py-2"
                  onClick={() => handleSingleAction("overwrite")}
                >
                  Overwrite
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto max-h-96 py-3 relative">
              <p className="px-4 mt-3 mb-2 text-gray-700">
                The following files already exist. What would you like to do?
              </p>
              {conflictedFiles.map((file, index) => (
                <div key={file.name} className="w-full px-4 py-2 ">
                  <div className="flex justify-between items-center border-gray-200  w-full">
                    <div className="flex space-x-3">
                      <strong>{file.name}</strong>
                      {processingIndex === index && (
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                      )}
                    </div>

                    {doneFiles.includes(file.name) ? (
                      <div className="min-w-[60px] text-right">
                        <span className="text-green-600 border bg-green-100 border-green-600 rounded-full px-2 h-5 flex items-center justify-center text-xs font-semibold">
                          Done
                        </span>
                      </div>
                    ) : failedFiles.includes(file.name) ? (
                      <div className="min-w-[60px] text-right">
                        <span className="text-red-600 border bg-red-100 border-red-600 rounded-full px-2 h-5 flex items-center justify-center text-xs font-semibold">
                          Failed
                        </span>
                      </div>
                    ) : conflictActions[file.name] == "cancel" &&
                      isProcessing ? (
                      <div className="min-w-[60px] text-right">
                        <span className="text-red-600 border bg-red-100 border-red-600 rounded-full px-2 h-5 flex items-center justify-center text-xs font-semibold">
                          Cancelled
                        </span>
                      </div>
                    ) : (
                      <div className="flex space-x-2 overflow-visible">
                        {(["cancel", "copy", "overwrite"] as const).map(
                          (action) => (
                            <ConflictActionButton
                              key={action}
                              action={action}
                              isSelected={conflictActions[file.name] === action}
                              onSelect={() => onActionChange(file.name, action)}
                              disabled={processingIndex !== null}
                            />
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {!isProcessing && (
              <div className="flex justify-between px-4 pt-4 border-t border-gray-300">
                <button
                  type="button"
                  className="text-white bg-red-600 rounded px-4 py-2"
                  onClick={dismissConflictModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`bg-green-600 text-white rounded px-4 py-2 ${
                    disabled ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={disabled}
                  onClick={handleMultiFileProcess}
                >
                  Proceed
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConflictModal;
